import { caseTaskSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import { mediaSchema } from "@/components/Forms/schema/mediaSchema.ts";
import { noteSchema } from "@/components/Forms/schema/note/noteSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";

const resourceSelector = createResourceCollectionSelector({
    type: "note",
    attributesSchema: noteSchema,
    relationships: {
        caseTask: {
            relationshipType: "one_nullable",
            include: {
                type: "caseTask",
                attributesSchema: caseTaskSchema,
            },
        },
        media: {
            relationshipType: "many",
            include: {
                type: "media",
                attributesSchema: mediaSchema,
            },
            required: true,
        },
    },
});

export const notesSelector = createDataSelector(resourceSelector);
export type Notes = ReturnType<typeof notesSelector>;
export type Note = Notes[number];

type Props = {
    caseIdUUID: string;
};

export const useFetchNotesForCase = ({ caseIdUUID }: Props): UseQueryResult<Notes> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-notes", caseIdUUID],
        queryFn: async () => {
            const requestBody = {
                caseIdUUID: caseIdUUID,
            };

            const response = await fetch(apiUrl("/note/for-case"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: notesSelector,
        staleTime: 10_0000,
    });
};
